// @ts-expect-error
import { animate, scroll, type Easing } from "motion";
import { homeHeroImages } from "@/data/home-hero-images";
import { VIDEO_URLS } from "@/data/videos";
import s from "./style.module.css";
static function getWidthMultiplier(width: number) {
    return width >= 960 ? 0.16 : width >= 480 ? 0.25 : 0.35;
}
// Video State
<let/isVisible=undefined as boolean | undefined>
<let/systemPaused=true>
<let/userPaused=undefined as boolean | undefined>
// Video Derived
<const/showButton=(isVisible !== undefined ? `${isVisible}` : undefined)>
<const/paused=(userPaused ?? systemPaused)>
// Animation State
<let/numImages=5>
<let/halfViewWidth=0>
<let/halfViewHeight=0>
<let/padding=0>
<let/halfCardWidth=0>
<let/halfCardHeight=0>
<let/baseOffsetX=0>
<let/widthEnd=0>
// Animation Derived
<const/cardWidth=halfCardWidth * 2>
<const/cardHeight=halfCardHeight * 2>
<const/baseOffsetY=halfViewHeight - halfCardHeight - padding>
<const/cardRefs: (() => HTMLDivElement)[]=[]>

<div/scrollRef class=s.scroll>
    <div class=s.sticky>
        <div class=s.container>
            <for|src, i| of=homeHeroImages>
                <div/cardRef class=s.card>
                    <img/imageRef src=src alt="">
                    <if=i === 0>
                        <video/videoRef
                            src=(VIDEO_URLS.homeHero.src)
                            poster=(VIDEO_URLS.homeHero.poster)
                            muted
                            loop
                            playsinline
                            data-user-paused=`${userPaused}`
                            onPlay() {
                                systemPaused = false;
                            }
                            onPause() {
                                systemPaused = true;
                            }/>
                        <play-button
                            aria-label=`${paused ? "Resume" : "Pause"} video`
                            paused=paused
                            visible=isVisible
                            class=s.button
                            onClick() {
                                if (paused) videoRef().play();
                                else videoRef().pause();

                                userPaused = videoRef().paused;
                            }/>
                    </if>
                </div>
                <script>
                    cardRefs.push(cardRef);
                    const prm = window.matchMedia(
                        "(prefers-reduced-motion: reduce)",
                    ).matches;
                    const video = cardRef().querySelector("video");
                    if (video) {
                        systemPaused = video.paused;
                        const observer = new IntersectionObserver(
                            ([entry]) => {
                                const isUserPaused =
                                    video.dataset.userPaused === "true";
                                if (
                                    !isUserPaused &&
                                    !prm &&
                                    entry.isIntersecting
                                ) {
                                    video.play();
                                } else if (!video.paused) {
                                    video.pause();
                                }
                            },
                            {
                                threshold: 0,
                                rootMargin: "0px 0px -99% 0px",
                            },
                        );
                        observer.observe(scrollRef());
                        $signal.onabort = () => {
                            if (!observer) return;
                            observer.unobserve(scrollRef());
                            observer.disconnect();
                        };
                    }
                </script>
            </for>
        </div>
    </div>
    <div/scrollTargetRef class=s.scrollTarget/>
</div>

<script>
    let prevWidth = 0;
    //
    const setViewportDimensions = () => {
        const currentWidth = window.innerWidth;
        if (currentWidth === prevWidth) return;
        prevWidth = currentWidth;
        //
        const isPortrait = window.innerWidth / window.innerHeight < 1;
        const isWidescreen = window.innerWidth >= 960;
        const styles = window.getComputedStyle(cardRef());
        const _padding = parseInt(styles.getPropertyValue("--padding"));
        const widthMultiplier = getWidthMultiplier(window.innerWidth);
        const _halfViewWidth = window.innerWidth / 2;
        const xOffMultiplier = _halfViewWidth >= 480 ? 1 : 0.5;
        let _halfCardWidth = window.innerWidth * widthMultiplier;
        if (window.innerWidth >= 1640) {
            _halfCardWidth = 264;
            baseOffsetX = 520;
            widthEnd = 1640 - _padding * 2;
        } else {
            baseOffsetX =
                _halfViewWidth - _halfCardWidth * xOffMultiplier - _padding;
            widthEnd = window.innerWidth - 2 * _padding;
        }
        //
        numImages = isWidescreen ? 5 : 4;
        halfViewWidth = _halfViewWidth / 2;
        halfViewHeight = window.innerHeight / 2;
        padding = _padding;
        halfCardWidth = _halfCardWidth;
        halfCardHeight =
            isPortrait || isWidescreen
                ? (_halfCardWidth / 4) * 5
                : _halfCardWidth;
    };
    //
    setViewportDimensions();
    window.addEventListener("resize", setViewportDimensions, {
        signal: $signal,
    });
</script>
<script>
    const cancelers = cardRefs.flatMap((ref, i) => {
        const step = -1 + (2 / (numImages - 1)) * i;
        const startOffsetX = step * baseOffsetX;
        const startOffsetY = step * baseOffsetY;
        const scaleEnd = (numImages - i) / numImages;
        const heightEnd = window.innerHeight - 2 * padding;
        const sizeKeyframes =
            i === 0
                ? {
                      width: [cardWidth, cardWidth, widthEnd],
                      height: [cardHeight, cardHeight, heightEnd],
                  }
                : {
                      width: [cardWidth],
                      height: [cardHeight],
                  };
        //
        const cancelMain = scroll(
            animate(
                ref(),
                {
                    x: [startOffsetX, 0, 0],
                    y: [startOffsetY, 0, 0],
                    scale: [1, scaleEnd, scaleEnd],
                    ...sizeKeyframes,
                },
                {
                    ease: [0.65, 0, 0.35, 1],
                },
            ),
            {
                target: scrollRef(),
                offset: ["start start", "end end"],
            },
        );
        //
        const image = ref().querySelector("img");
        if (!image) return [cancelMain];
        //
        const cancelImage = scroll(
            animate(
                image,
                {
                    opacity: [1, 0, 0, 0],
                },
                {
                    ease: [0.65, 0, 0.35, 1],
                },
            ),
            {
                target: scrollRef(),
                offset: ["start start", "end end"],
            },
        );
        //
        return [cancelMain, cancelImage];
    });
    //
    $signal.onabort = () => {
        cancelers.forEach((fn) => fn());
    };
</script>
<script>
    const scrollTarget = scrollTargetRef();
    const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) isVisible = true;
        else isVisible = false;
    });
    observer.observe(scrollTarget);
    $signal.onabort = () => {
        observer.unobserve(scrollTarget);
        observer.disconnect();
    };
</script>
